import { useEffect, useRef } from 'react';
import { useElementsCallback } from './useElementsCallback';

export interface ActiveHeading {
  depth: 2 | 3;
  text: string;
}

export interface UseActiveHeadingOnScrollOptions {
  forwardRef: React.RefObject<HTMLElement>;
  getHeadingKey?: (heading: HTMLHeadingElement) => string | null;
  scrollContainerRef: React.RefObject<HTMLElement>;
  onActiveHeading: (heading: ActiveHeading | null, element?: HTMLHeadingElement) => void;
}

export const useActiveHeadingOnScroll = (options: UseActiveHeadingOnScrollOptions) => {
  const { forwardRef, getHeadingKey, scrollContainerRef, onActiveHeading } = options;
  const headingsRef = useRef<HTMLHeadingElement[]>([]);

  useElementsCallback<HTMLHeadingElement>({
    forwardRef,
    selectors: ['h2', 'h3'],
    deps: [scrollContainerRef, onActiveHeading],
    onElement: (element) => {
      if (element.innerText.trim() === '') {
        return;
      }

      headingsRef.current.push(element);

      return () => {
        headingsRef.current = headingsRef.current.filter((heading) => heading !== element);
      };
    }
  });

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    const updateActiveHeading = () => {
      const headings = headingsRef.current.filter((heading) => {
        if (heading.innerText.trim() === '') {
          return false;
        }

        return getHeadingKey ? getHeadingKey(heading) !== null : true;
      });
      if (headings.length === 0) {
        onActiveHeading(null);
        return;
      }

      const scrollContainerTop = scrollContainer.getBoundingClientRect().top;
      const current = headings
        .slice()
        .reverse()
        .find((heading) => heading.getBoundingClientRect().top - scrollContainerTop <= 120);

      const activeHeading = current ?? headings[0];
      onActiveHeading(
        {
          depth: Number(activeHeading.tagName.substring(1)) as 2 | 3,
          text: activeHeading.innerText
        },
        activeHeading
      );
    };

    updateActiveHeading();
    scrollContainer.addEventListener('scroll', updateActiveHeading, { passive: true });

    return () => {
      scrollContainer.removeEventListener('scroll', updateActiveHeading);
    };
  }, [getHeadingKey, scrollContainerRef, onActiveHeading]);
};
